import { useState } from 'react';
import { api } from '@aithora/shared';
import { useSession } from '../session/SessionContext';
import { useQuery } from '../useQuery';
import { useReadOnly } from '../ui/ReadOnly';

/** Look for a test page sent to you this often. */
export const TEST_PAGE_POLL_MS = 20_000;

/**
 * A test page sent to the signed-in person (the setup checklist's "Send a test alert",
 * a test push) says "open Aithora and tap I got it". This is that tap: the newest test
 * sent to you in the last 30 minutes that you have not acknowledged
 * (GET /notify/test/pending), and "I got it" acknowledges it (POST /notify/test/:id/ack),
 * which is what proves the page reached a person. Never shown in a read-only view.
 */
export function TestPageBanner() {
  const { client, session } = useSession();
  const readOnly = useReadOnly();
  const enabled = !!session && !readOnly;
  const q = useQuery((signal) => api.testPending(client, { signal }), [client], { enabled, pollMs: TEST_PAGE_POLL_MS });
  const [done, setDone] = useState<string | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const t = q.data?.tests?.find((x) => x.testId !== done) ?? null;
  if (!enabled || !t) return null;
  const ack = async () => {
    setFailed(null);
    try {
      await api.testAck(client, { params: { id: t.testId }, body: {} });
      setDone(t.testId);
      q.reload();
    } catch (e) {
      setFailed(e instanceof Error ? e.message : 'That did not go through.');
    }
  };
  return (
    <div role="status" className="embanner embanner--test" data-testid="test-page-banner">
      <span className="embanner__text">
        {`Test page${t.fromName ? ` from ${t.fromName}` : ''}. No resident is involved.`}
        {failed && <span className="embanner__err">{` (${failed})`}</span>}
      </span>
      <button type="button" className="embanner__go" onClick={ack}>
        I got it
      </button>
    </div>
  );
}
